/* eslint-disable */
import type { IssuedBankAccountBankAccountState } from "../../banklin/issued_bank_account.pb";
import type { Reason } from "../customers/deletion/Reason.pb";
import type { LendingStatus } from "../lending/LendingStatusProto.pb";
import type { Article, ArticleType } from "./data.pb";

export enum OutboundPhoneCallOutcome {
  ANSWERED = "ANSWERED",
  NO_ANSWER = "NO_ANSWER",
  VOICEMAIL = "VOICEMAIL",
}

export enum LightningCaseType {
  EMAIL = "EMAIL",
  CALLBACK = "CALLBACK",
  CHAT = "CHAT",
  APPAREL = "APPAREL",
  CASH_CARD_DISPUTE = "CASH_CARD_DISPUTE",
  P2P_PAYMENT_DISPUTE = "P2P_PAYMENT_DISPUTE",
  CASH_APP_PAY_DISPUTE = "CASH_APP_PAY_DISPUTE",
  ATO_TRANSFER = "ATO_TRANSFER",
}

export enum LightningCaseStatus {
  PENDING_CREATION = "PENDING_CREATION",
  NEW = "NEW",
  OPEN = "OPEN",
  PENDING = "PENDING",
  RESOLVED = "RESOLVED",
  CLOSED = "CLOSED",
  DUPLICATE = "DUPLICATE",
}

export enum LightningCaseOrigin {
  APP = "APP",
  TOOLBOX = "TOOLBOX",
  SPRINKLR = "SPRINKLR",
  APP_CHAT = "APP_CHAT",
  APPAREL_SUPPORTER = "APPAREL_SUPPORTER",
  CONCIERGE = "CONCIERGE",
  /** INBOUND_VOICE - call to inbound 1800 number */
  INBOUND_VOICE = "INBOUND_VOICE",
  COMPLAINTS = "COMPLAINTS",
  DISPUTRON = "DISPUTRON",
  INVESTING = "INVESTING",
  /** APP_CALLBACK_REQUEST - outbound phone/callback request, via Support Home in app */
  APP_CALLBACK_REQUEST = "APP_CALLBACK_REQUEST",
}

export enum LightningCaseEntityType {
  PAYMENT = "PAYMENT",
  TRANSACTION = "TRANSACTION",
  TRANSFER = "TRANSFER",
  INVESTING_ORDER = "INVESTING_ORDER",
  PAY_WITH_CASH_PAYMENT = "PAY_WITH_CASH_PAYMENT",
  INTERNAL_TRANSFER = "INTERNAL_TRANSFER",
}

export enum BusinessUnit {
  CASH_APP = "CASH_APP",
  CASH_LENDING = "CASH_LENDING",
  CASH_APP_UK = "CASH_APP_UK",
  CASH_INVESTING = "CASH_INVESTING",
}

export enum GlobalTaxonomy {
  ABUSIVE_CUSTOMER = "ABUSIVE_CUSTOMER",
  ALLEGED_EMPLOYEE_OR_COMPANY_MISCONDUCT = "ALLEGED_EMPLOYEE_OR_COMPANY_MISCONDUCT",
  ATTORNEY_DEMAND_LETTER = "ATTORNEY_DEMAND_LETTER",
  COMPLAINT_VIA_GOVERNMENT_OFFICIAL = "COMPLAINT_VIA_GOVERNMENT_OFFICIAL",
  CONCLUDING_RESPONSE = "CONCLUDING_RESPONSE",
  CONSUMER_RIGHTS_VIOLATION = "CONSUMER_RIGHTS_VIOLATION",
  DISCRIMINATION = "DISCRIMINATION",
  EXECUTIVE_COMPLAINT = "EXECUTIVE_COMPLAINT",
  INTELLECTUAL_PROPERTY_INFRINGEMENT = "INTELLECTUAL_PROPERTY_INFRINGEMENT",
  PRE_LITIGATION_DEMAND = "PRE_LITIGATION_DEMAND",
  PRIVACY_VIOLATION = "PRIVACY_VIOLATION",
  REMEDIATED_SUPPORT = "REMEDIATED_SUPPORT",
  REPEAT_COMPLAINT_WO_RESPONSE_RESOLUTION = "REPEAT_COMPLAINT_WO_RESPONSE_RESOLUTION",
  REPUTATIONAL_RISK = "REPUTATIONAL_RISK",
  ALLEGED_REGULATORY_VIOLATION = "ALLEGED_REGULATORY_VIOLATION",
  EXPRESSION_OF_DISSATISFACTION = "EXPRESSION_OF_DISSATISFACTION",
}

export enum ComplaintTier {
  COMPLAINT_TIER_UNKNOWN = "COMPLAINT_TIER_UNKNOWN",
  TIER_1 = "TIER_1",
  TIER_2 = "TIER_2",
  TIER_3 = "TIER_3",
}

export enum NumberStatusDecision {
  ALLOW = "ALLOW",
  THROTTLE = "THROTTLE",
  DENY = "DENY",
}

export interface GetArticleRequest {
  /** One of token or slug must be provided. */
  token?: string;
  slug?: string;
  region?: string;
  locale?: string;
  type?: ArticleType;
  show_all?: boolean;
}

export interface GetArticleResponse {
  article?: Article;
}

export interface ListArticlesRequest {
  type?: ArticleType;
  show_all?: boolean;
}

export interface ListArticlesResponse {
  articles?: Article[];
}

export interface UpdateArticleRequest {
  article?: Article;
  index?: boolean;
}

export interface UpdateArticleResponse {
}

export interface DeleteArticleRequest {
  token?: string;
  region?: string;
  locale?: string;
}

export interface DeleteArticleResponse {
}

export interface SalesforceClassicCase {
  salesforce_id?: string;
  subject?: string;
  case_created_at?: number;
}

export interface CreateLightningSupportCaseRequest {
  customer_token?: string;
  flow_token?: string;
  case_type?: LightningCaseType;
  customer_name?: string;
  first_name?: string;
  last_name?: string;
  email_address?: string;
  phone_number?: string;
  country_code?: string;
  language_code?: string;
  subject?: string;
  description?: string;
  support_category_selection_token?: string;
  entity_id?: string;
  entity_type?: LightningCaseEntityType;
  entity_amount_cents?: number;
  entity_currency_code?: string;
  priority_level?: number;
  auto_response_category?: string;
  routing_group?: string;
  routing_category?: string;
  previous_classic_cases?: SalesforceClassicCase[];
  transfer_reason?: string;
  origin?: LightningCaseOrigin;
  intake_owner_id?: string;
  voice_pii_verification_metrics?: VoicePIIVerificationMetrics;
  originating_case_salesforce_id?: string;
  amazon_connect_contact_id?: string;
}

export interface CreateLightningSupportCaseResponse {
  status?: CreateLightningSupportCaseResponseStatus;
  flow_token?: string;
}

export enum CreateLightningSupportCaseResponseStatus {
  SUCCESS = "SUCCESS",
  ALREADY_CREATED = "ALREADY_CREATED",
  AMAZON_CONNECT_CONTACT_ID_EXISTS = "AMAZON_CONNECT_CONTACT_ID_EXISTS",
}

export interface LightningCase {
  customer_token?: string;
  customer_name?: string;
  first_name?: string;
  last_name?: string;
  email_address?: string;
  phone_number?: string;
  description?: string;
  subject?: string;
  flow_token?: string;
  salesforce_id?: string;
  type?: LightningCaseType;
  skills?: LightningSkillLevel[];
  language_code?: string;
  country_code?: string;
  entity_id?: string;
  entity_type?: LightningCaseEntityType;
  entity_amount_cents?: number;
  entity_currency_code?: string;
  status?: LightningCaseStatus;
  priority_level?: number;
  auto_response_category?: string;
  routing_group?: string;
  selected_category?: string;
  routing_category?: string;
  parent_id?: number;
  id?: number;
  previous_classic_cases?: SalesforceClassicCase[];
  origin?: LightningCaseOrigin;
  transfer_reason?: string;
  complaints?: LightningComplaint[];
  intake_owner_id?: string;
  created_at?: number;
  updated_at?: number;
  salesforce_case_number?: string;
  salesforce_originating_case_id?: string;
  app_platform?: string;
  app_version?: string;
  verification_passed?: boolean;
  call_outcome?: OutboundPhoneCallOutcome;
  ml_attributes?: LightningCaseMLAttribute[];
  notice_of_error_at?: number;
  is_reg_e?: boolean;
  /** @deprecated */
  metadata?: LightningCaseMetadata;
  amazon_connect_contact_id?: string;
}

export interface LightningCaseMetadata {
  amazon_connect_contact_id?: string;
}

export interface GetLightningCaseRequest {
  flow_token?: string;
  salesforce_id?: string;
}

export interface GetLightningCaseResponse {
  lightning_case?: LightningCase;
}

export interface GetLightningCaseCurrentStateRequest {
  salesforce_id?: string;
}

export interface GetLightningCaseCurrentStateResponse {
  id?: string;
  supplied_email?: string;
  case_status?: string;
  subject?: string;
  priority?: string;
  description?: string;
  language?: string;
  country_code?: string;
  routing_group?: string;
  customer_token?: string;
  current_channel?: string;
  origin?: string;
  current_group?: string;
}

export interface GetSalesforceClassicCaseRequest {
  salesforce_id?: string;
}

export interface GetSalesforceClassicCaseResponse {
  classic_case?: SalesforceClassicCase;
  emails?: GetSalesforceClassicCaseResponseEmail[];
}

export interface GetSalesforceClassicCaseResponseEmail {
  id?: string;
  content?: string;
  sender?: string;
  sent_at?: number;
}

export interface UpdatePhoneCaseRequest {
  customer_token?: string;
  flow_token?: string;
  case_id?: string;
  verification_passed?: boolean;
  call_outcome?: OutboundPhoneCallOutcome;
  voice_pii_verification_metrics?: VoicePIIVerificationMetrics;
  intake_owner_id?: string;
}

export interface UpdatePhoneCaseResponse {
}

export interface LightningAvailabilityRequest {
  customer_token?: string;
  support_category_selection_token?: string;
}

export interface LightningAvailabilityResponse {
  has_open_callback_case?: boolean;
  callback_queue_size?: number;
  eligible?: boolean;
  voice_callback?: LightningAvailabilityResponseVoiceCallback;
  chat?: LightningAvailabilityResponseChat;
}

export interface LightningAvailabilityResponseVoiceCallback {
  available?: LightningAvailabilityResponseVoiceCallbackAvailable | undefined;
  too_busy?: LightningAvailabilityResponseVoiceCallbackTooBusy | undefined;
  unavailable?: LightningAvailabilityResponseVoiceCallbackUnavailable | undefined;
  source_phone_number?: string;
}

export interface LightningAvailabilityResponseVoiceCallbackAvailable {
  expected_wait_time_ms?: number;
}

export interface LightningAvailabilityResponseVoiceCallbackTooBusy {
  expected_wait_time_ms?: number;
}

export interface LightningAvailabilityResponseVoiceCallbackUnavailable {
}

export interface LightningAvailabilityResponseChat {
  /**
   * Chat will be available if
   * 1. customer has has been enabled for chat
   * 2. customer is not blocked
   * 3. the given node allows chat
   */
  available?: boolean;
}

export interface GetChatUserPoolRequest {
}

export interface GetChatUserPoolResponse {
  blocked_users?: number;
  current_pool_size?: number;
}

export interface UpdateChatUserOnboardingRateRequest {
  target_hourly_onboarding_rate?: number;
}

export interface UpdateChatUserOnboardingRateResponse {
}

export interface CreateChatCustomerRequest {
  customer_token?: string;
}

export interface CreateChatCustomerResponse {
}

export interface GetOpenConversationsRequest {
}

/** deprecated */
export interface GetOpenConversationsResponse {
  conversations?: GetOpenConversationsResponseConversation[];
}

export interface GetOpenConversationsResponseConversation {
  customer_token?: string;
  conversation_token?: string;
  opened_at?: number;
  solved_at?: number;
  lightning_case?: GetOpenConversationsResponseConversationLightningCase;
}

export interface GetOpenConversationsResponseConversationLightningCase {
  salesforce_id?: string;
}

export interface SearchConversationsRequest {
  criteria?: SearchConversationsRequestCriteria;
}

export interface SearchConversationsRequestCriteria {
  closed?: boolean;
  customer_token?: string;
  autoresponse_token?: string;
}

export interface SearchConversationsResponse {
  conversations?: SearchConversationsResponseConversation[];
}

export interface SearchConversationsResponseConversation {
  customer_token?: string;
  conversation_token?: string;
  opened_at?: number;
  solved_at?: number;
  closed_at?: number;
  lightning_case?: SearchConversationsResponseConversationLightningCase;
}

export interface SearchConversationsResponseConversationLightningCase {
  salesforce_id?: string;
}

export interface UpdateConversationRequest {
  customer_token?: string;
  conversation_token?: string;
  action?: UpdateConversationRequestAction;
}

export enum UpdateConversationRequestAction {
  SOLVE = "SOLVE",
  CLOSE = "CLOSE",
}

export interface UpdateConversationResponse {
}

export interface BlockChatCustomerRequest {
  customer_token?: string;
}

export interface BlockChatCustomerResponse {
}

export interface GetConversationRequest {
  customer_token?: string;
  conversation_token?: string;
  /** Gets a page of messages after and including the message passed, or first page if none passed */
  after_message_token?: string;
}

export interface GetConversationResponse {
  messages?: GetConversationResponseChatMessage[];
  conversation?: GetConversationResponseConversation;
  more_messages_after?: boolean;
}

export interface GetConversationResponseChatMessage {
  /** Canonical ID of message */
  message_token?: string;
  /** Time that the message was seen by the server */
  recorded_at?: number;
  /** ID set by client when creating message */
  idempotence_token?: string;
  text?: GetConversationResponseChatMessageTextBody | undefined;
  file?: GetConversationResponseChatMessageFileBody | undefined;
  sunshine_file?: GetConversationResponseChatMessageSunshineFileBody | undefined;
  transaction?: GetConversationResponseChatMessageTransactionBody | undefined;
  customer?: GetConversationResponseChatMessageCustomer | undefined;
  bot?: GetConversationResponseChatMessageBot | undefined;
  advocate?: GetConversationResponseChatMessageAdvocate | undefined;
  quick_text_title?: string;
}

export interface GetConversationResponseChatMessageTextBody {
  text?: string;
}

export interface GetConversationResponseChatMessageFileBody {
  text?: string;
  file_token?: string;
  mime_type?: string;
  file_name?: string;
}

export interface GetConversationResponseChatMessageSunshineFileBody {
  text?: string;
  file_url?: string;
  mime_type?: string;
  file_name?: string;
}

export interface GetConversationResponseChatMessageTransactionBody {
  entity_id?: string;
}

export interface GetConversationResponseChatMessageCustomer {
  app_platform?: string;
  app_version?: string;
  app_device_id?: string;
}

export interface GetConversationResponseChatMessageBot {
}

export interface GetConversationResponseChatMessageAdvocate {
  username?: string;
}

/**
 * TODO: @mmckeough: if we decide to keep the timestamp,
 *   add a status oneof and deprecate started_waiting_at
 */
export interface GetConversationResponseConversation {
  status?: GetConversationResponseConversationStatus;
  started_waiting_at?: number;
}

export enum GetConversationResponseConversationStatus {
  UNKNOWN = "UNKNOWN",
  OPEN = "OPEN",
  RESOLVED = "RESOLVED",
  CLOSED = "CLOSED",
  /** @deprecated */
  INACTIVE = "INACTIVE",
  WAITING_ON_CUSTOMER_REPLY = "WAITING_ON_CUSTOMER_REPLY",
  CUSTOMER_IDLE = "CUSTOMER_IDLE",
}

export interface SendChatMessagesRequest {
  customer_token?: string;
  conversation_token?: string;
  messages?: SendChatMessagesRequestOutgoingMessage[];
  advocate_username?: string;
}

export interface SendChatMessagesRequestOutgoingMessage {
  idempotence_token?: string;
  text?: SendChatMessagesRequestOutgoingMessageTextBody | undefined;
  action?: SendChatMessagesRequestOutgoingMessageActionBody | undefined;
  quick_text_info?: SendChatMessagesRequestOutgoingMessageQuickTextInfo;
}

export interface SendChatMessagesRequestOutgoingMessageTextBody {
  text?: string;
}

export interface SendChatMessagesRequestOutgoingMessageOpenLink {
  url?: string;
}

export interface SendChatMessagesRequestOutgoingMessageSelectTransaction {
  payload?: Uint8Array;
}

export interface SendChatMessagesRequestOutgoingMessageActionBody {
  title?: string;
  subtitle?: string;
  open_link?: SendChatMessagesRequestOutgoingMessageOpenLink | undefined;
  select_transaction?: SendChatMessagesRequestOutgoingMessageSelectTransaction | undefined;
}

export interface SendChatMessagesRequestOutgoingMessageQuickTextInfo {
  title?: string;
  /** Stable, human-readable ID for the QuickText */
  slug?: string;
  /** Language of the QuickText content */
  locale?: string;
}

export interface SendChatMessagesResponse {
}

export interface SetChatWaitingOnCustomerReplyRequest {
  waiting_on_customer_reply?: boolean;
  customer_token?: string;
  conversation_token?: string;
  /** @deprecated */
  advocate_username?: string;
  advocate?: SetChatWaitingOnCustomerReplyRequestAdvocate;
}

export interface SetChatWaitingOnCustomerReplyRequestAdvocate {
  username?: string;
  user_id?: string;
}

export interface SetChatWaitingOnCustomerReplyResponse {
}

export interface CreateLightningSkillRequest {
  name?: string;
  min_level?: number;
  max_level?: number;
}

export interface CreateLightningSkillResponse {
  skill?: LightningSkill;
}

export interface UpdateLightningSkillRequest {
  name?: string;
  min_level?: number;
  max_level?: number;
}

export interface UpdateLightningSkillResponse {
  skill?: LightningSkill;
}

export interface LightningSkill {
  name?: string;
  salesforce_id?: string;
  min_level?: number;
  max_level?: number;
}

export interface GetSkillsRequest {
}

export interface GetSkillsResponse {
  skills?: LightningSkill[];
}

export interface LightningSkillLevel {
  name?: string;
  level?: number;
  salesforce_id?: string;
}

export interface SupportCategory {
  selection_token?: string;
  skills?: LightningSkillLevel[];
  routing_category?: string;
  inbound_voice_enabled?: boolean;
  omni_channel_enabled?: boolean;
  parent_selection_token?: string;
  title?: string;
  body_text?: string;
  criteria?: string;
  visible?: boolean;
  priority?: number;
  auto_response_category?: string;
  action_client_scenario?: string;
  action_url?: string;
  emphasis_type?: string;
  contact_enabled?: boolean;
  include_payment?: boolean;
  callback_enabled?: boolean;
  routing_group?: string;
  is_current_issue?: boolean;
  skip_to_contact?: boolean;
  chat_enabled?: boolean;
  is_searchable?: boolean;
  region?: SupportCategoryRegion;
  suggestion_token?: string;
  exclude_from_recently_viewed?: boolean;
}

export enum SupportCategoryRegion {
  /** GLOBAL - Applies to all regions */
  GLOBAL = "GLOBAL",
  USA = "USA",
  GBR = "GBR",
  AUS = "AUS",
  CAN = "CAN",
  IRL = "IRL",
}

export interface GetAvailableSupportCriteriaRequest {
}

export interface GetAvailableSupportCriteriaResponse {
  available_criteria?: string[];
}

export interface GetSupportCategoriesRequest {
}

export interface GetSupportCategoriesResponse {
  support_categories?: SupportCategory[];
}

export interface GetContentfulSupportCategoriesRequest {
}

export interface GetContentfulSupportCategoriesResponse {
  support_categories?: GetContentfulSupportCategoriesResponseContentfulSupportCategory[];
}

export interface GetContentfulSupportCategoriesResponseContentfulSupportCategory {
  selection_token?: string;
  title_en?: string;
}

export interface ResetSupportCategoriesRequest {
  support_categories?: SupportCategory[];
  /**
   * Adding this as backup field since the automagically generated
   * WebActions UI doesn't allow filling out repeated fields
   */
  support_categories_raw_json?: string;
}

export interface ResetSupportCategoriesResponse {
}

export interface UpdateSupportCategoryRequest {
  support_category?: SupportCategory;
}

export interface UpdateSupportCategoryResponse {
}

export interface ArchiveSupportCategoryRequest {
  selection_token?: string;
}

export interface ArchiveSupportCategoryResponse {
}

export interface OrderSupportCategoriesRequest {
  selection_tokens?: string[];
}

export interface OrderSupportCategoriesResponse {
}

export interface GetSupportCategoryRequest {
  selection_token?: string;
}

export interface GetSupportCategoryResponse {
  /** Will be null if requested category was not found. */
  support_category?: SupportCategory;
}

export interface GetSupportCategoryRecommendationsRequest {
  support_categories?: GetSupportCategoryRecommendationsRequestSupportCategory[];
  context?: GetSupportCategoryRecommendationsRequestContext;
}

export interface GetSupportCategoryRecommendationsRequestSupportCategory {
  selection_token?: string;
  criteria?: string;
}

export interface GetSupportCategoryRecommendationsRequestContext {
  customer_token?: string;
  entity_id?: string;
  user_agent?: string;
  flow_token?: string;
}

export interface GetSupportCategoryRecommendationsResponse {
  selection_tokens?: string[];
}

export interface UpdateCustomerLendingStatusRequest {
  customer_token?: string;
  lending_status?: LendingStatus;
  version?: number;
}

export interface UpdateCustomerLendingStatusResponse {
}

export interface BatchUpdateCustomerDDARequest {
  customer_dda_list?: BatchUpdateCustomerDDARequestCustomerDDA[];
}

export interface BatchUpdateCustomerDDARequestCustomerDDA {
  customer_token?: string;
  dda_list?: BatchUpdateCustomerDDARequestCustomerDDADDA[];
  had_ldda?: boolean;
}

export interface BatchUpdateCustomerDDARequestCustomerDDADDA {
  fidelius_token?: string;
  bank_acct_state?: IssuedBankAccountBankAccountState;
  version?: number;
}

export interface BatchUpdateCustomerDDAResponse {
}

export interface LogComplaintForCaseRequest {
  case_id?: number;
  description?: string;
  initial_complaint_date?: number;
  record_type_id?: number;
  state?: string;
  salesforce_case_id?: string;
  logged_by?: string;
  status?: string;
  valid?: string;
  end_complaint_date?: number;
  external_complaint_id?: string;
  business_unit?: BusinessUnit;
  flow_token?: string;
  global_taxonomy?: GlobalTaxonomy;
}

export interface InvestmentLightningComplaint {
  intake_channel?: InvestmentLightningComplaintIntakeChannel;
  security_symbol_1?: string;
  security_symbol_2?: string;
  security_symbol_3?: string;
  dispute_amount_cents?: number;
  start_allegation_date?: string;
  end_allegation_date?: string;
  legal_name?: string;
}

export enum InvestmentLightningComplaintIntakeChannel {
  EMAIL = "EMAIL",
  MAIL = "MAIL",
  SOCIAL_MEDIA = "SOCIAL_MEDIA",
  FINRA = "FINRA",
  SEC = "SEC",
  STATE_SECURITY_REGULATORS = "STATE_SECURITY_REGULATORS",
  BBB = "BBB",
  CFPB = "CFPB",
  STATE_ATTORNEY_GENERAL = "STATE_ATTORNEY_GENERAL",
  FTC = "FTC",
  EMAIL_INCLUDING_CONTACT_SUPPORT_MESSAGES = "EMAIL_INCLUDING_CONTACT_SUPPORT_MESSAGES",
  OTHER = "OTHER",
}

export interface InvestmentLightningComplaintRequest {
  customer_name?: string;
  first_name?: string;
  last_name?: string;
  intake_channel?: InvestmentLightningComplaintIntakeChannel;
  security_symbol_1?: string;
  security_symbol_2?: string;
  security_symbol_3?: string;
  dispute_amount_cents?: number;
  start_allegation_date?: string;
  end_allegation_date?: string;
  legal_name?: string;
}

export interface BBBLightningComplaint {
  intake_channel?: BBBLightningComplaintIntakeChannel;
}

export enum BBBLightningComplaintIntakeChannel {
  EMAIL = "EMAIL",
  MAIL = "MAIL",
  SOCIAL_MEDIA = "SOCIAL_MEDIA",
  FINRA = "FINRA",
  SEC = "SEC",
  STATE_SECURITY_REGULATORS = "STATE_SECURITY_REGULATORS",
  BBB = "BBB",
  CFPB = "CFPB",
  STATE_ATTORNEY_GENERAL = "STATE_ATTORNEY_GENERAL",
  FTC = "FTC",
  OTHER = "OTHER",
}

export interface BBBLightningComplaintRequest {
  intake_channel?: BBBLightningComplaintIntakeChannel;
}

export interface RegulatoryLightningComplaint {
  intake_channel?: RegulatoryLightningComplaintIntakeChannel;
  agency?: RegulatoryLightningComplaintAgency;
  /** if agency == OTHER */
  other_agency?: string;
  date_received_by_CERT?: string;
  jira_ticket?: string;
  complainant_full_name?: string;
  date_response_due?: string;
  duplicate_complaint?: boolean;
  pushback?: boolean;
  doc_title?: string;
  complaint_uploaded?: boolean;
}

export enum RegulatoryLightningComplaintAgency {
  /** CFPB_ - Underscore is used to distinguish from IntakeChannel.CFPB */
  CFPB_ = "CFPB_",
  CA_DFPI = "CA_DFPI",
  NY_DFS = "NY_DFS",
  MI_DIFS = "MI_DIFS",
  FL_OFR = "FL_OFR",
  PA_DB = "PA_DB",
  OTHER_AGENCY = "OTHER_AGENCY",
}

export enum RegulatoryLightningComplaintIntakeChannel {
  EMAIL = "EMAIL",
  MAIL = "MAIL",
  SOCIAL_MEDIA = "SOCIAL_MEDIA",
  FINRA = "FINRA",
  SEC = "SEC",
  STATE_SECURITY_REGULATORS = "STATE_SECURITY_REGULATORS",
  BBB = "BBB",
  CFPB = "CFPB",
  STATE_ATTORNEY_GENERAL = "STATE_ATTORNEY_GENERAL",
  FTC = "FTC",
  REGULATORY = "REGULATORY",
  SES = "SES",
  AGENCY_PORTAL = "AGENCY_PORTAL",
  OTHER = "OTHER",
}

export interface RegulatoryLightningComplaintRequest {
  intake_channel?: RegulatoryLightningComplaintIntakeChannel;
  agency?: RegulatoryLightningComplaintAgency;
  /** if agency == OTHER */
  other_agency?: string;
  date_received_by_CERT?: string;
  jira_ticket?: string;
  complainant_full_name?: string;
  date_response_due?: string;
  duplicate_complaint?: boolean;
  pushback?: boolean;
  doc_title?: string;
  complaint_uploaded?: boolean;
}

export interface LitigationLightningComplaint {
  intake_channel?: LitigationLightningComplaintIntakeChannel;
  source_type?: LitigationLightningComplaintSourceType;
  attorney_contact_info?: string;
  document_date?: string;
  date_response_due?: string;
  contact_alias?: string;
  jira_ticket?: string;
  /** hyperlink can be at at most 2048 chars */
  gdrive?: string;
  counsel_notes?: string;
  country?: string;
  complainant_full_name?: string;
  contact_team_dri?: string;
  internal_actions?: string;
  attorney_state?: string;
}

export enum LitigationLightningComplaintIntakeChannel {
  EMAIL = "EMAIL",
  MAIL = "MAIL",
  SOCIAL_MEDIA = "SOCIAL_MEDIA",
  FINRA = "FINRA",
  SEC = "SEC",
  STATE_SECURITY_REGULATORS = "STATE_SECURITY_REGULATORS",
  BBB = "BBB",
  CFPB = "CFPB",
  STATE_ATTORNEY_GENERAL = "STATE_ATTORNEY_GENERAL",
  FTC = "FTC",
  LITIGATION = "LITIGATION",
  OTHER = "OTHER",
}

export enum LitigationLightningComplaintSourceType {
  AG = "AG",
  ADL = "ADL",
  DL = "DL",
  INVESTIGATION_ONLY = "INVESTIGATION_ONLY",
  IRON_CITY = "IRON_CITY",
}

export interface LitigationLightningComplaintRequest {
  intake_channel?: LitigationLightningComplaintIntakeChannel;
  source_type?: LitigationLightningComplaintSourceType;
  attorney_contact_info?: string;
  document_date?: string;
  date_response_due?: string;
  contact_alias?: string;
  jira_ticket?: string;
  /** hyperlink can be at at most 2048 chars */
  gdrive?: string;
  counsel_notes?: string;
  country?: string;
  complainant_full_name?: string;
  contact_team_dri?: string;
  internal_actions?: string;
  attorney_state?: string;
}

export interface UkAndIrelandLightningComplaint {
  c_token?: string;
  intake_channel?: UkAndIrelandLightningComplaintIntakeChannel;
  country?: string;
  date_acknowledged?: number;
  external_due_date?: number;
  complainant_full_name?: string;
  salesforce_case_number?: number;
}

export enum UkAndIrelandLightningComplaintIntakeChannel {
  CHAT = "CHAT",
  EMAIL = "EMAIL",
  SOCIAL_MEDIA = "SOCIAL_MEDIA",
  MAIL = "MAIL",
  LITIGATION = "LITIGATION",
  FOS = "FOS",
  FSPO = "FSPO",
  OTHER = "OTHER",
}

export interface UkAndIrelandLightningComplaintRequest {
  c_token?: string;
  intake_channel?: UkAndIrelandLightningComplaintIntakeChannel;
  country?: string;
  date_acknowledged?: number;
  external_due_date?: number;
  complainant_full_name?: string;
  salesforce_case_number?: number;
}

export interface LogComplaintForCaseResponse {
  status?: LogComplaintForCaseResponseStatus;
}

export enum LogComplaintForCaseResponseStatus {
  UNKNOWN = "UNKNOWN",
  SUCCESS = "SUCCESS",
}

export interface LightningComplaint {
  id?: number;
  lightning_case_id?: number;
  salesforce_id?: string;
  description?: string;
  initial_complaint_date?: number;
  record_type_id?: number;
  state?: string;
  logged_by?: string;
  status?: LightningComplaintLightningComplaintStatus;
  valid?: LightningComplaintLightningComplaintValid;
  tier?: ComplaintTier;
  end_complaint_date?: number;
  external_complaint_id?: string;
  business_unit?: BusinessUnit;
  ciphertext_?: Uint8Array;
}

export enum LightningComplaintLightningComplaintStatus {
  STATUS_UNKNOWN = "STATUS_UNKNOWN",
  /** @deprecated */
  PENDING = "PENDING",
  NEW = "NEW",
  OPEN = "OPEN",
  RESOLVED = "RESOLVED",
}

export enum LightningComplaintLightningComplaintValid {
  VALID_UNKNOWN = "VALID_UNKNOWN",
  VALID = "VALID",
  INVALID = "INVALID",
}

export interface GetChatQuickTextsRequest {
  customer_token?: string;
}

export interface GetChatQuickTextsResponse {
  quick_texts?: GetChatQuickTextsResponseQuickText[];
  languages?: GetChatQuickTextsResponseLanguage[];
}

export interface GetChatQuickTextsResponseQuickText {
  title?: string;
  keywords?: string[];
  /**
   * identifies the language of the quicktext eg "en-US"
   * Any value here will also appear in the `languages` list
   */
  locale?: string;
  /** Stable, human-readable ID for the QuickText */
  slug?: string;
}

export interface GetChatQuickTextsResponseLanguage {
  locale?: string;
  display_name?: string;
}

export interface ListChatQuicktextLocalesRequest {
}

export interface ListChatQuicktextLocalesResponse {
  locales?: string[];
}

export interface ListChatQuickTextsRequest {
  locale?: string;
}

export interface ListChatQuickTextsResponse {
  quick_texts?: ListChatQuickTextsResponseQuickText[];
}

export interface ListChatQuickTextsResponseQuickText {
  title?: string;
  keywords?: string;
  document_token?: string;
  locale?: string;
  body?: string;
  problem_type?: string;
  problem_tag?: string;
  /** Stable, human-readable ID for the QuickText */
  slug?: string;
}

export interface PrepareChatQuickTextRequest {
  customer_token?: string;
  conversation_token?: string;
  title?: string;
  /** Language of the QuickText content. Must match a value returned by `GetChatQuickTextResponse`. */
  locale?: string;
  /** Stable, human-readable ID for the QuickText. If provided, will be used instead of "title". */
  slug?: string;
}

export interface PrepareChatQuickTextResponse {
  title?: string;
  body?: string;
  /** Stable, human-readable ID for the QuickText */
  slug?: string;
  /** Language of the QuickText content */
  locale?: string;
}

export interface ComplaintProspect {
  id?: number;
  lightning_case_id?: number;
  message?: string;
  /** context is the substring where the complaint is believed to occur */
  context?: string;
  tier?: ComplaintTier;
  category?: string;
  matching_terms?: ComplaintTerm[];
  lightning_case_salesforce_id?: string;
  ciphertext_?: Uint8Array;
}

export interface ComplaintTerm {
  id?: number;
  value?: string;
  tier?: ComplaintTier;
  category?: string;
  description?: string;
}

export interface GetComplaintProspectsRequest {
  case_id?: number;
}

export interface GetComplaintProspectsResponse {
  complaints?: ComplaintProspect[];
}

export interface GetInboundNumberStatusRequest {
  number?: string;
}

export interface GetInboundNumberStatusResponse {
  decision?: NumberStatusDecision;
  reason?: string;
  reason_code?: string;
}

export interface BlockInboundNumberRequest {
  number?: string;
}

export interface BlockInboundNumberResponse {
  result?: BlockInboundNumberResponseStatus;
}

export enum BlockInboundNumberResponseStatus {
  SUCCESS = "SUCCESS",
  ALREADY_BLOCKED = "ALREADY_BLOCKED",
}

export interface UnblockInboundNumberRequest {
  number?: string;
}

export interface UnblockInboundNumberResponse {
  result?: UnblockInboundNumberResponseStatus;
}

export enum UnblockInboundNumberResponseStatus {
  SUCCESS = "SUCCESS",
  NOT_BLOCKED = "NOT_BLOCKED",
}

export interface RoutingGroup {
  name?: string;
  omni_channel_enabled?: boolean;
}

export interface GetRoutingGroupsResponse {
  routing_groups?: RoutingGroup[];
}

export interface RoutingCategory {
  name?: string;
  omni_channel_enabled?: boolean;
}

export interface GetRoutingCategoriesResponse {
  routing_categories?: RoutingCategory[];
}

export interface BackfillPrimaryBankingCustomerRequest {
  primary_banking_customers?: BackfillPrimaryBankingCustomerRequestPrimaryBankingCustomer[];
}

export interface BackfillPrimaryBankingCustomerRequestPrimaryBankingCustomer {
  customer_token?: string;
  primary_banking_status?: BackfillPrimaryBankingCustomerRequestPrimaryBankingCustomerPrimaryBankingStatus;
  version?: number;
}

export enum BackfillPrimaryBankingCustomerRequestPrimaryBankingCustomerPrimaryBankingStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

export interface BackfillPrimaryBankingCustomerResponse {
}

export interface UpdateCustomersRequest {
  updates?: UpdateCustomersRequestCustomerUpdate[];
}

export interface UpdateCustomersRequestCustomerUpdate {
  version?: number;
  /** Encoded com.squareup.protos.franklin.data.Customer */
  customer?: Uint8Array;
}

export interface UpdateCustomersResponse {
}

export interface UpdateLightningSupportCaseCustomerTokenRequest {
  customer_token?: string;
  id?: number;
}

export interface UpdateLightningSupportCaseCustomerTokenResponse {
  lightning_case?: LightningCase;
}

export interface VoicePIIVerificationMetrics {
  passed?: boolean;
  restart_count?: number;
  full_name_match_count?: number;
  full_name_mismatch_count?: number;
  phone_number_match_count?: number;
  phone_number_mismatch_count?: number;
  email_match_count?: number;
  email_mismatch_count?: number;
  postal_code_match_count?: number;
  postal_code_mismatch_count?: number;
  bank_match_count?: number;
  bank_mismatch_count?: number;
  debit_card_match_count?: number;
  debit_card_mismatch_count?: number;
  cash_card_match_count?: number;
  cash_card_mismatch_count?: number;
  payments_match_count?: number;
  payments_mismatch_count?: number;
}

export interface GetAutoResponsesMetadataRequest {
}

export interface GetAutoResponsesMetadataResponse {
  autoresponse_metadata?: GetAutoResponsesMetadataResponseAutoResponseMetadata[];
}

export interface GetAutoResponsesMetadataResponseAutoResponseAction {
  /** Auto response text we serve customers in chat */
  text?: string[];
  /** Type of action "LINK" or "SELECT_TRANSACTION" */
  action_type?: string;
  /** Title text to display for Action */
  action_title?: string;
  /** Subtitle text to display on Action */
  action_subtitle?: string;
  /** Link to scenario within application */
  action_url?: string;
}

export interface GetAutoResponsesMetadataResponseAutoResponseMetadata {
  /** Human readable description of the auto response issue */
  issue_name?: string;
  /** Whether the auto response is active in production or not */
  is_active?: boolean;
  /** Riskarbiter rule location */
  rule_url?: string;
  /** Auto response text we serve customers in chat */
  autoresponse_copy_text?: string;
  region?: string;
  /**
   * Readable hook def token name representing an auto response category
   * eg. LINK_FAILURE_CARD_INVALID_ZIPCODE
   */
  autoresponse_category?: string;
  /** User who last updated the Riskarbiter rule */
  last_updated_user_name?: string;
  /** Auto response token under which the copy is stored */
  autoresponse_token?: string;
  /** text and deep link we serve customers in chat */
  auto_response_action?: GetAutoResponsesMetadataResponseAutoResponseAction;
}

export interface BackfillCashAppPayActivityRequest {
  customer_tokens?: string[];
}

export interface BackfillCashAppPayActivityResponse {
  failed_customer_tokens?: string[];
}

export interface GetCaseAssignmentsRequest {
  customer_token?: string;
  advocate_ldap?: string;
}

export interface GetCaseAssignmentsResponse {
  case_assignments?: GetCaseAssignmentsResponseCaseAssignment[];
}

export interface GetCaseAssignmentsResponseCaseAssignment {
  salesforce_case_number?: string;
}

export interface CapturePhoneNumberEndResult {
  phone_number?: string;
}

export interface GetConversationMLMetadataRequest {
  customer_token?: string;
  conversation_token?: string;
  advocate_username?: string;
}

export interface GetConversationMLMetadataResponse {
  intents?: GetConversationMLMetadataResponseIntent[];
  quick_texts?: GetConversationMLMetadataResponseQuickText[];
  auto_responses?: GetConversationMLMetadataResponseAutoResponse[];
  ml_summary?: string;
}

export interface GetConversationMLMetadataResponseKnowledgeArticle {
  name?: string;
  url?: string;
}

export interface GetConversationMLMetadataResponseIntent {
  name?: string;
  description?: string;
  knowledge_articles?: GetConversationMLMetadataResponseKnowledgeArticle[];
}

export interface GetConversationMLMetadataResponseQuickText {
  name?: string;
  /** Stable, human-readable ID for the QuickText */
  slug?: string;
  /** Language of the QuickText content */
  locale?: string;
}

export interface GetConversationMLMetadataResponseAutoResponse {
  /** The token of the auto response that prompted the reply */
  token?: string;
  /** The messages of the the auto response */
  messages?: GetConversationMLMetadataResponseAutoResponseMessage[];
  /** The quick reply the user chose */
  selected_quick_reply?: GetConversationMLMetadataResponseQuickReplyOption;
  /** The list of quick reply options */
  all_quick_reply_options?: GetConversationMLMetadataResponseQuickReplyOption[];
}

export interface GetConversationMLMetadataResponseAutoResponseMessage {
  /** The regions for the auto response */
  regions?: string[];
  /** The texts of the messages */
  texts?: string[];
}

export interface GetConversationMLMetadataResponseQuickReplyOption {
  /** The token for the quick reply */
  token?: string;
  /** The text of the quick reply */
  text?: string;
}

export interface GetRecommendedQuickTextsRequest {
  customer_token?: string;
  conversation_token?: string;
  /** Get recommendations for the conversation up to this message token (inclusive) */
  message_token?: string;
  /** Uses IETF BCP 47 eg: "en-US" */
  locale?: string;
  advocate?: GetRecommendedQuickTextsRequestAdvocate;
}

export interface GetRecommendedQuickTextsRequestAdvocate {
  /** aka LDAP */
  username?: string;
  /** Registry's UID (not employee id) */
  user_id?: string;
}

export interface GetRecommendedQuickTextsResponse {
  /** Echo back the message token used in the request */
  message_token?: string;
  quick_texts?: GetRecommendedQuickTextsResponseQuickText[];
}

export interface GetRecommendedQuickTextsResponseQuickText {
  title?: string;
  keywords?: string[];
  /** Stable, human-readable ID for the QuickText */
  slug?: string;
  /** identifies the language of the quicktext eg "en-US" */
  locale?: string;
}

export interface RequestPrivacyDeletionRequest {
  customer_token?: string;
  reason?: Reason;
}

export interface RequestPrivacyDeletionResponse {
}

export interface PruneSupportCriteriaNodesRequest {
  nodes?: PruneSupportCriteriaNodesRequestNode[];
  context?: PruneSupportCriteriaNodesRequestContext;
}

export interface PruneSupportCriteriaNodesRequestNode {
  id?: string;
  criteria?: string;
  children?: PruneSupportCriteriaNodesRequestNode[];
}

export interface PruneSupportCriteriaNodesRequestHeader {
  name?: string;
  value?: string;
}

export interface PruneSupportCriteriaNodesRequestContext {
  customer_token?: string;
  entity_id?: string;
  /** @deprecated */
  headerNamesAndValues?: string[];
  user_agent?: string;
  flow_token?: string;
}

export interface PruneSupportCriteriaNodesResponse {
  node_ids?: string[];
}

export interface GetRecommendedComplaintTaxonomyRequest {
  salesforce_id?: string;
}

export interface GetRecommendedComplaintTaxonomyResponse {
  global_taxonomy?: GlobalTaxonomy;
}

export interface GetLightningCaseSupportMessagesRequest {
  salesforce_id?: string;
}

export interface GetLightningCaseSupportMessagesResponse {
  support_messages?: GetLightningCaseSupportMessagesResponseSupportMessage[];
}

export interface GetLightningCaseSupportMessagesResponseSupportMessage {
  text?: string;
  channel?: GetLightningCaseSupportMessagesResponseSupportMessageSupportChannel;
  sender?: GetLightningCaseSupportMessagesResponseSupportMessageMessageSender;
  created_at?: number;
}

export enum GetLightningCaseSupportMessagesResponseSupportMessageSupportChannel {
  CHANNEL_UNKNOWN = "CHANNEL_UNKNOWN",
  EMAIL = "EMAIL",
  CHAT = "CHAT",
  VOICE = "VOICE",
  FEED_ITEM = "FEED_ITEM",
}

export enum GetLightningCaseSupportMessagesResponseSupportMessageMessageSender {
  SENDER_UNKNOWN = "SENDER_UNKNOWN",
  CUSTOMER = "CUSTOMER",
  AGENT = "AGENT",
  BOT = "BOT",
}

export interface LightningCaseEntity {
  id?: number;
  created_at?: number;
  updated_at?: number;
  lightning_case_id?: number;
  entityId?: string;
  entityType?: LightningCaseEntityType;
  isLinked?: boolean;
  linked_by_user_id?: string;
  linked_by_customer?: boolean;
}

export interface GetLightningCaseEntitiesRequest {
  case_id?: number;
}

export interface GetLightningCaseEntitiesResponse {
  entities?: LightningCaseEntity[];
}

export interface CreateLightningCaseEntity {
  entityId?: string;
  entityType?: LightningCaseEntityType;
}

export interface CreateLightningCaseEntitiesRequest {
  lightning_case_flow_token?: string;
  lightning_case_entities?: CreateLightningCaseEntity[];
  loggedInUser?: CreateLightningCaseEntitiesRequestLoggedInUser;
}

export interface CreateLightningCaseEntitiesRequestLoggedInUser {
  /** aka LDAP */
  username?: string;
  /** Registry's UID (not employee id) */
  user_id?: string;
}

export interface CreateLightningCaseEntitiesResponse {
  entityLinkingFailures?: CreateLightningCaseEntityFailure[];
  successfullyLinkedEntityIds?: string[];
}

export interface UnlinkLightningCaseEntityRequest {
  lightning_case_flow_token?: string;
  entity_id?: string;
  loggedInUser?: UnlinkLightningCaseEntityRequestLoggedInUser;
}

export interface UnlinkLightningCaseEntityRequestLoggedInUser {
  /** aka LDAP */
  username?: string;
  /** Registry's UID (not employee id) */
  user_id?: string;
}

export interface UnlinkLightningCaseEntityResponse {
  unlinked_entity_id?: string;
}

export interface CreateLightningCaseEntityFailure {
  entity?: CreateLightningCaseEntity;
  failureReason?: CreateLightningCaseEntityFailureEntityLinkingFailure;
}

export enum CreateLightningCaseEntityFailureEntityLinkingFailure {
  ENTITY_ALREADY_LINKED = "ENTITY_ALREADY_LINKED",
  UNABLE_TO_PARSE_CASH_TOKEN = "UNABLE_TO_PARSE_CASH_TOKEN",
  CASH_TOKEN_TYPE_NOT_SUPPORTED = "CASH_TOKEN_TYPE_NOT_SUPPORTED",
  /** BAD_REQUEST_DATA - EntityId or FlowToken is null for entity request. */
  BAD_REQUEST_DATA = "BAD_REQUEST_DATA",
  ENTITY_NOT_FOUND = "ENTITY_NOT_FOUND",
}

export interface GetWritingAssistantV1SuggestedReplyRequest {
  /**
   * An id generated by the front-end that will identify each instance of requesting a WA
   * suggested reply. This UUID is used to trace the lifecycle for the suggested reply
   */
  request_uuid?: string;
  customer_token?: string;
  conversation_token?: string;
  /** Get the suggested reply for the conversation up to this message token (inclusive) */
  up_to_message_token?: string;
  /** e.g. 'en', 'es', 'fr'. The region will be inferred from the customer. This is NOT a locale */
  language?: string;
  /** Which QT should the WA generate a reply for? */
  quick_text_slug?: string;
  advocate?: GetWritingAssistantV1SuggestedReplyRequestAdvocate;
}

export interface GetWritingAssistantV1SuggestedReplyRequestAdvocate {
  /** aka LDAP */
  username?: string;
  /** Registry's UID (not employee id) */
  user_id?: string;
}

export interface GetWritingAssistantV1SuggestedReplyResponse {
  /** Echoed back from the request */
  request_uuid?: string;
  up_to_message_token?: string;
  language?: string;
  quick_text_slug?: string;
  /** The request is not eligible for the feature */
  not_eligible?:
    | GetWritingAssistantV1SuggestedReplyResponseNotEligible
    | undefined;
  /**
   * The request is eligible but the writing assistant was not able to generate a suggested reply
   * This can happen because of holdout, or being able to generate a response of enough quality
   */
  no_suggested_reply?:
    | GetWritingAssistantV1SuggestedReplyResponseNoSuggestedReply
    | undefined;
  /** Happy path - a suggested reply was found */
  suggested_reply?: GetWritingAssistantV1SuggestedReplyResponseSuggestedReply | undefined;
}

export interface GetWritingAssistantV1SuggestedReplyResponseNotEligible {
}

export interface GetWritingAssistantV1SuggestedReplyResponseNoSuggestedReply {
}

export interface GetWritingAssistantV1SuggestedReplyResponseSuggestedReply {
  text?: string;
}

export interface LightningCaseMLAttribute {
  attribute_category?: string;
  attribute_value?: string;
}

export interface CreateRetrospectiveAutoResponseJobRequest {
  job_name?: string;
}

export interface CreateRetrospectiveAutoResponseJobResponse {
}

export interface Supportal {
  GetSupportCategories(request: GetSupportCategoriesRequest): Promise<GetSupportCategoriesResponse>;
  GetSupportCategory(request: GetSupportCategoryRequest): Promise<GetSupportCategoryResponse>;
  GetSupportCategoryRecommendations(
    request: GetSupportCategoryRecommendationsRequest,
  ): Promise<GetSupportCategoryRecommendationsResponse>;
  UpdateCustomers(request: UpdateCustomersRequest): Promise<UpdateCustomersResponse>;
  RequestPrivacyDeletion(request: RequestPrivacyDeletionRequest): Promise<RequestPrivacyDeletionResponse>;
  PruneSupportCriteriaNodes(request: PruneSupportCriteriaNodesRequest): Promise<PruneSupportCriteriaNodesResponse>;
  GetContentfulSupportCategories(
    request: GetContentfulSupportCategoriesRequest,
  ): Promise<GetContentfulSupportCategoriesResponse>;
}
